import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  billableVisits: PropTypes.array,
  onInvoicesCreate: PropTypes.func.isRequired,
};

const defaultProps = {
  title: '',
  billableVisits: [],
};

@autobind
class TableBillableVisits extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      creatingInvoices: false,
    };
  }

  async onCreateInvoicesClick() {
    const selectedVisitIds = this.dataTables.getSelectedRowKeys();
    if (selectedVisitIds.length > 0) {
      try {
        this.setState({
          creatingInvoices: true,
        });

        const params = { visitIds: selectedVisitIds };
        const numOfInvoices = await this.callServiceForLong('/invoice/create_invoices', params);

        this.notifySuccess('Success', `${numOfInvoices} invoices have been created.`);

        // Re-search.
        this.props.onInvoicesCreate();
      } finally {
        this.setState({
          creatingInvoices: false,
        });
      }
    } else {
      this.notifyWarning('Warning', 'You need to select at least one visit to create an invoice.');
    }
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'ascend',
    }, {
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
    }, {
      title: 'Company',
      dataIndex: 'companyName',
      sorter: (a, b) => a.companyName?.localeCompare(b.companyName),
    }, {
      title: 'Service Code',
      dataIndex: 'serviceCode',
      sorter: (a, b) => a.serviceCode?.localeCompare(b.serviceCode),
    }, {
      title: 'Patient',
      dataIndex: 'patientName',
      sorter: (a, b) => a.patientName?.localeCompare(b.patientName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/patients/patient_profile?id=${record.patientId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Caregiver',
      dataIndex: 'caregiverName',
      sorter: (a, b) => a.caregiverName?.localeCompare(b.caregiverName),
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          href={`/caregivers/caregiver_profile?id=${record.caregiverId}`}
          target="_blank"
        >
          {text}
        </styledComp.A>
      ),
    }, {
      title: 'Sch. Times',
      dataIndex: 'scheduleTimes',
      sorter: (a, b) => a.fromTime - b.fromTime,
    }, {
      title: 'Visit Times',
      dataIndex: 'visitTimes',
      sorter: (a, b) => a.visitIn - b.visitIn,
    }, {
      title: 'Bill Hours',
      dataIndex: 'billHoursString',
      sorter: (a, b) => a.billHours - b.billHours,
    }, {
      title: 'Unit Type',
      dataIndex: 'unitType',
      sorter: (a, b) => a.unitType?.localeCompare(b.unitType),
    }, {
      title: 'Unit Rate',
      dataIndex: 'unitRateString',
      sorter: (a, b) => a.unitRate - b.unitRate,
    }, {
      title: 'Amount',
      dataIndex: 'amountString',
      sorter: (a, b) => a.amount - b.amount,
    }];
  }

  createDataSource(billableVisits, reasons) {
    const dataSource = [];
    billableVisits.forEach((visit, index) => {
      dataSource.push({
        key: visit.getId(),
        id: visit.getId(),
        date: visit.getLong('date'),
        dateUsString: visit.getString('dateUsString'),
        companyName: visit.getString('companyName'),
        serviceCode: visit.getRemoteObject('service')?.getString('displayCode'),
        patientId: visit.getLong('patientId'),
        patientName: visit.getString('patientName'),
        caregiverId: visit.getLong('caregiverId'),
        caregiverName: visit.getString('caregiverName'),
        fromTime: visit.getLong('fromTime'),
        scheduleTimes: `${visit.getString('fromTimeString')}-${visit.getString('toTimeString')}`,
        visitIn: visit.getLong('visitIn'),
        visitTimes: `${visit.getString('visitInString') || ''}-${visit.getString('visitOutString') || ''}`,
        billHours: visit.getLong('billHours'),
        billHoursString: visit.getString('billHoursString'),
        unitType: this.e2t('serviceUnitType', visit.getRemoteObject('service')?.getString('unitType')),
        unitRate: visit.getRemoteObject('service')?.getDouble('unitRate'),
        unitRateString: helpers.toDollars(visit.getRemoteObject('service')?.getDouble('unitRate')),
        amount: visit.getDouble('amount'),
        amountString: helpers.toDollars(visit.getDouble('amount')),
      });
    });
    return dataSource;
  }

  renderFooter() {
    const { creatingInvoices } = this.state;

    return (
      <FlexRowContainer
        width="match_parent"
        height="wrap_content"
        padding="20px 20px"
        justifyContent="center"
        backgroundColor="containerGrayBackground"
      >
        <CustomButton
          width="wrap_content"
          height="32px"
          backgroundColor="yellow_gold"
          fontSize="14px"
          type="primary"
          loading={creatingInvoices}
          onClick={this.onCreateInvoicesClick}
        >
          CREATE INVOICES FOR SELECTED VISITS
        </CustomButton>
      </FlexRowContainer>
    );
  }

  renderContent() {
    const { billableVisits, title, onInvoicesCreate, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="yellow_gold"
        fontSize="12px"
        size="small"
        title={title}
        icon="file-text"
        selectable
        columns={this.createColumns()}
        dataSource={this.createDataSource(billableVisits)}
        footer={this.renderFooter()}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableBillableVisits.propTypes = propTypes;
TableBillableVisits.defaultProps = defaultProps;

export default TableBillableVisits;
